import { useOperatorsOrdersQuery } from 'api/queries/operators';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import dayjs from 'dayjs';
import { LegSchemaType, UserPermission } from 'remote/global';
import { useEffect, useState } from 'react';
import { ModalContextType, OrderEditModal } from './OrderEditModal';

type WrappedOrderDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bookingNumber: string;
  rideId?: number;
  contextType: ModalContextType; // Specify whether it's an order or ride view
};

const getIsLastLegDateValid = (legs: LegSchemaType[]) => {
  if (!legs || legs?.length <= 0) return false;

  const lastLeg = legs[legs.length - 1];
  if (!lastLeg) return false;
  if (dayjs(new Date()) > dayjs(lastLeg.endTime)) return false;
  return true;
};

export function useOrderEditStatus({
  bookingNumber,
  rideId,
}: {
  bookingNumber: string;
  rideId: number;
}) {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { data: order } = useOperatorsOrdersQuery(bookingNumber);

  const { permissions = {} } = userInfo ?? {};
  const hasOrderEditPermissions =
    permissions.ORDER === UserPermission.WRITE_OWN ||
    permissions.ORDER === UserPermission.WRITE_ALL;

  const isOrderCancelled = order?.cancelledAt != null; // cancelledAt will only be available when order is cancelled
  const selectedRide = order?.rides.find((ride) => ride.id === rideId);
  const isLastLegDateValid = getIsLastLegDateValid(selectedRide?.legs ?? []);
  const isOrderEditAvailable = hasOrderEditPermissions && !isOrderCancelled && isLastLegDateValid;
  const isEditDisabled = !isLastLegDateValid;

  return { isOrderEditAvailable, isOrderCancelled, isEditDisabled };
}

function WrappedOrderDetailsModal({
  open,
  setOpen,
  onClose,
  bookingNumber,
  rideId,
  contextType,
}: WrappedOrderDetailsModalProps) {
  const [modalContext, setModalContext] = useState(contextType);

  const { isOrderEditAvailable } = useOrderEditStatus({ bookingNumber, rideId: rideId! });

  // Sync modalContext with contextType when modal is closed
  useEffect(() => {
    if (!open) {
      setModalContext(contextType);
    }
  }, [open, contextType]);

  // Handler for when booking number is clicked in ride view to open order view
  const handleOpenOrderView = () => {
    setModalContext(ModalContextType.ORDER_VIEW);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setModalContext(contextType);
    onClose();
  };

  return (
    <OrderEditModal
      open={open}
      setOpen={setOpen}
      onClose={handleClose}
      bookingNumber={bookingNumber}
      isOrderEditAvailable={isOrderEditAvailable}
      contextType={modalContext}
      rideId={rideId}
      onOpenOrderView={handleOpenOrderView}
    />
  );
}

export { WrappedOrderDetailsModal, ModalContextType };
